import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';

const Household = () => {
    const { studentId } = useParams();
    const navigate = useNavigate();
    const { get, post } = useApi();
    const { showToast } = useAppState();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [data, setData] = useState({
        student: null,
        mother: null,
        father: null,
        others: []
    });

    useEffect(() => {
        const fetchHousehold = async () => {
            try {
                const result = await get(`/api/admin/households/${studentId}/`);
                setData(result);
            } catch (err) {
                console.error(err);
                showToast('Failed to load household data', 'error');
            } finally {
                setLoading(false);
            }
        };
        fetchHousehold();
    }, [studentId, get, showToast]);

    const handleUpdate = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await post(`/api/admin/households/${studentId}/update/`, data);
            showToast('Household records synchronized successfully', 'success');
        } catch (err) {
            console.error(err);
            showToast('Failed to update household', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (section, field, value) => {
        setData(prev => ({
            ...prev,
            [section]: {
                ...(prev[section] || {}),
                [field]: value
            }
        }));
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
                <i className="fas fa-house-chimney-window text-4xl text-[#18216D]/20 animate-pulse"></i>
                <p className="text-[10px] font-black text-slate-300 uppercase tracking-[0.2em]">Maping Household Units...</p>
            </div>
        );
    }

    const sections = [
        { id: 'student', title: 'Scholastic Profile', icon: 'fa-user-graduate', color: 'indigo' },
        { id: 'mother', title: 'Maternal Guardian', icon: 'fa-person-dress', color: 'rose' },
        { id: 'father', title: 'Paternal Guardian', icon: 'fa-person', color: 'sky' }
    ];

    return (
        <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-700 pb-20">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-4xl font-black text-[#18216D] tracking-tighter uppercase italic">Household Management</h1>
                    <p className="text-slate-400 font-bold text-sm mt-1 flex items-center gap-2">
                        <i className="fas fa-link text-[#FFC425]"></i>
                        Unified Governance for <span className="text-[#18216D] underline decoration-indigo-200/50 underline-offset-4">{data.student?.name}</span>
                    </p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={() => navigate(-1)}
                        className="px-6 py-3 bg-white border border-slate-100 rounded-xl text-slate-400 font-black text-[10px] uppercase tracking-widest hover:text-[#18216D] hover:bg-slate-50 transition-all shadow-sm"
                    >
                        Back to Directory
                    </button>
                    <button
                        onClick={handleUpdate}
                        disabled={saving}
                        className="px-8 py-3 bg-[#18216D] text-white rounded-xl font-black text-[10px] uppercase tracking-[0.2em] shadow-xl shadow-indigo-900/20 hover:scale-[1.02] active:scale-95 transition-all flex items-center gap-3 disabled:opacity-50"
                    >
                        {saving ? <i className="fas fa-circle-notch animate-spin"></i> : <i className="fas fa-cloud-arrow-up"></i>}
                        {saving ? 'Synchronizing' : 'Commit Changes'}
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {sections.map(section => {
                    const entity = data[section.id];
                    if (!entity && section.id !== 'student') {
                        return (
                            <div key={section.id} className="bg-slate-50/50 border-2 border-dashed border-slate-100 rounded-[2.5rem] p-10 flex flex-col items-center justify-center text-center opacity-60">
                                <div className={`w-16 h-16 rounded-2xl bg-white border border-slate-100 flex items-center justify-center text-2xl text-slate-300 mb-4`}>
                                    <i className={`fas ${section.icon}`}></i>
                                </div>
                                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-2">{section.title}</h3>
                                <p className="text-[9px] font-bold text-slate-300 max-w-[150px]">No record linked to this household unit.</p>
                                <button className="mt-6 text-[9px] font-black text-[#18216D] uppercase tracking-widest border-b border-[#18216D]/20 hover:border-[#18216D] transition-all">
                                    + Link Record
                                </button>
                            </div>
                        );
                    }

                    return (
                        <div key={section.id} className="bg-white rounded-[2.5rem] shadow-2xl shadow-indigo-900/5 border border-slate-100 p-8 space-y-8 relative overflow-hidden group">
                             {/* Decorative Background Icon */}
                            <i className={`fas ${section.icon} absolute -right-6 -bottom-6 text-[120px] opacity-[0.02] group-hover:opacity-[0.04] transition-opacity`}></i>

                            <div className="flex items-center gap-4">
                                <div className={`w-14 h-14 rounded-2xl bg-${section.color}-50 text-${section.color}-600 flex items-center justify-center text-xl shadow-inner border border-white`}>
                                    <i className={`fas ${section.icon}`}></i>
                                </div>
                                <div>
                                    <h3 className="text-lg font-black text-[#18216D] uppercase italic leading-none">{section.title}</h3>
                                    <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest mt-1.5">{entity?.email || 'System Identity'}</p>
                                </div>
                            </div>

                            <div className="space-y-5">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">First Name</label>
                                        <input
                                            type="text"
                                            value={entity?.first_name || ''}
                                            onChange={(e) => handleChange(section.id, 'first_name', e.target.value)}
                                            className="w-full px-5 py-3.5 bg-slate-50 border-none rounded-xl focus:ring-2 focus:ring-[#18216D]/10 font-bold text-xs"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Last Name</label>
                                        <input
                                            type="text"
                                            value={entity?.last_name || ''}
                                            onChange={(e) => handleChange(section.id, 'last_name', e.target.value)}
                                            className="w-full px-5 py-3.5 bg-slate-50 border-none rounded-xl focus:ring-2 focus:ring-[#18216D]/10 font-bold text-xs"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Official Email</label>
                                    <input
                                        type="email"
                                        value={entity?.email || ''}
                                        onChange={(e) => handleChange(section.id, 'email', e.target.value)}
                                        className="w-full px-5 py-3.5 bg-slate-50 border-none rounded-xl focus:ring-2 focus:ring-[#18216D]/10 font-bold text-xs"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Connect Phone</label>
                                    <input
                                        type="text"
                                        value={entity?.phone || ''}
                                        onChange={(e) => handleChange(section.id, 'phone', e.target.value)}
                                        className="w-full px-5 py-3.5 bg-slate-50 border-none rounded-xl focus:ring-2 focus:ring-[#18216D]/10 font-bold text-xs"
                                    />
                                </div>



                                {section.id === 'student' && (
                                    <div className="space-y-2 pt-2 border-t border-slate-50">
                                        <label className="text-[9px] font-black text-[#FFC425] uppercase tracking-widest ml-1">Career Pathway Track</label>
                                        <select
                                            value={entity?.career_pathway || ''}
                                            onChange={(e) => handleChange(section.id, 'career_pathway', e.target.value)}
                                            className="w-full px-5 py-3.5 bg-indigo-50/50 border border-indigo-100 text-[#18216D] rounded-xl font-black text-[10px] uppercase tracking-widest"
                                        >
                                            <option value="">No Pathway Assigned</option>
                                            <option value="STEM">STEM (Sci, Tech, Eng, Math)</option>
                                            <option value="SOCIAL_SCIENCES">Social Sciences (Humanities)</option>
                                            <option value="ARTS_SPORTS">Arts and Sports Science</option>
                                        </select>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            
            {data.others?.length > 0 && (
                <div className="bg-slate-50 border border-slate-100 rounded-[2.5rem] p-8">
                    <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
                        <i className="fas fa-users-viewfinder text-indigo-400"></i>
                        Additional Family Linkages
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {data.others.map(other => (
                            <div key={other.id} className="bg-white p-4 rounded-2xl border border-slate-200/50 shadow-sm flex items-center gap-4">
                                <div className="h-10 w-10 bg-indigo-50 rounded-xl flex items-center justify-center text-xs font-black text-[#18216D]">
                                    {other.name.charAt(0)}
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[11px] font-black text-[#18216D] truncate">{other.name}</p>
                                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{other.role}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Household;
